/**
 * Permissions Hook
 * 
 * Provides utilities for checking user permissions and filtering navigation
 */

import { useMemo } from 'react';
import { usePermissions } from '@/providers/PermissionsProvider';

// Map navigation item titles/hrefs to permission screen keys
const navItemToPermissionMap: Record<string, string> = {
  // Main navigation items
  'Dashboard': 'dashboard',
  '/dashboard': 'dashboard',
  'Assets': 'assets',
  'Asset Records': 'assets',
  '/assets': 'assets',
  'Tasks': 'tasks',
  'Work Items': 'tasks',
  '/tasks': 'tasks',
  '/tasks/home': 'tasks',
  'Help & Support': 'help',
  '/help': 'help',

  // Transactions
  'Additions': 'additions',
  '/transactions/additions': 'additions',
  'Depreciation': 'depreciation',
  '/transactions/depreciation': 'depreciation',
  'Revaluation': 'revaluation',
  '/transactions/revaluation': 'revaluation',
  'Disposals': 'disposals',
  '/transactions/disposals': 'disposals',

  // Management
  'Check-In / Check-Out': 'checkinout',
  '/checkinout': 'checkinout',
  '/checkinout/calendar': 'checkinout',
  '/checkinout/checkout': 'checkinout',
  '/checkinout/settings': 'checkinout',
  'Compliance': 'compliance',
  '/compliance': 'compliance',
  '/compliance/assets': 'compliance',
  '/compliance/regulations': 'compliance',
  '/compliance/schedule': 'compliance',

  // Audits
  'Audit Execution': 'audits-auditor',
  'Audit Management': 'audits-manager',
  'Audits': 'audits-manager',
  'Create Audit': 'audits-manager',
  'Active Audits': 'audits-manager',
  'Scheduled Audits': 'audits-manager',
  'Audit History': 'audits-manager',
  'Exceptions': 'audits-manager',
  '/audits': 'audits-manager',
  '/audits/dashboard': 'audits-manager',
  '/audits/active': 'audits-manager',
  '/audits/scheduled': 'audits-manager',
  '/audits/history': 'audits-manager',
  '/audits/exceptions': 'audits-manager',
  '/audits/create': 'audits-manager',

  // Reports
  'Insights': 'insights',
  '/insights': 'insights',
  'Reports & Analytics': 'insights',
  'Insurance Value Report': 'insights',
  'Statutory Report': 'insights',
  '/reports/insurance-value': 'insights',
  '/reports/statutory': 'insights',
  'Asset History': 'enquiry',
  'Transaction History': 'enquiry',
  '/enquiry': 'enquiry',
  'Audit Trail': 'audit-trail',
  '/audit': 'audit-trail',

  // Data Management
  'Import Assets': 'import-assets',
  '/administration/imports': 'import-assets',
  '/assets/import': 'import-assets',
  '/assets/import/csv': 'import-assets',
  '/assets/import/accounting-sync': 'import-assets',
  '/assets/import/transaction-scan': 'import-assets',
  'Import Transactions': 'import-transactions',
  '/administration/import-transactions': 'import-transactions',

  // Configuration
  'Locations': 'locations',
  '/administration/locations': 'locations',
  'Categories': 'categories',
  '/administration/categories': 'categories',
  'Equipment': 'equipment',
  '/administration/equipment': 'equipment',
  'Disposal Reasons': 'disposal-reasons',
  '/administration/disposal-reasons': 'disposal-reasons',

  // Settings
  'Team Settings': 'team-settings',
  'Team Permissions': 'team-settings',
  '/administration/teams-permissions': 'team-settings',
  'Feature Settings': 'feature-settings',
  '/settings/features': 'feature-settings',
  'Asset Options': 'asset-options',
  '/settings/asset-options': 'asset-options',
  'Period Management': 'period-management',
  '/settings/period-management': 'period-management',
  'Integration Log': 'integration-log',
  '/settings/integration-log': 'integration-log',
};

// Map parent navigation items to their child permission categories
const parentNavToCategory: Record<string, string[]> = {
  'Transactions': ['additions', 'depreciation', 'revaluation', 'disposals'],
  'Management': ['checkinout', 'compliance'],
  'Compliance': ['compliance'],
  'Audits': ['audits-auditor', 'audits-manager'],
  'Reports': ['insights', 'enquiry', 'audit-trail'],
  'Reports & Analytics': ['insights', 'enquiry', 'audit-trail'],
  'Data Management': ['import-assets', 'import-transactions'],
  'Administration': ['import-assets', 'locations', 'categories', 'equipment', 'disposal-reasons'],
  'Configuration': ['locations', 'categories', 'equipment', 'disposal-reasons'],
  'Settings': ['team-settings', 'feature-settings', 'asset-options', 'period-management', 'integration-log'],
  'System Settings': ['team-settings', 'feature-settings', 'asset-options', 'period-management', 'integration-log'],
};

export interface NavItem {
  title: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  children?: NavItem[];
  badge?: {
    count: number;
    variant?: 'default' | 'critical';
  };
}

// Navigation items that should always be visible regardless of permissions
// These are general utility screens that don't require specific access rights
const alwaysAccessibleItems: Set<string> = new Set([
  'Help & Support',
  '/help',
  'help',
]);

/**
 * Hook to check if user can access a specific navigation item
 */
export function useCanAccessNavItem() {
  const { userPermissions, permissions, loading, permissionsEnforced = true } = usePermissions();

  function hasPermission(screenKey: string): boolean {
    if (!userPermissions) return false;
    const permDef = permissions.find(p => p.screenKey === screenKey);
    return !!(permDef && userPermissions.grantedPermissions.includes(permDef.id));
  }

  function checkChildren(item: NavItem): boolean {
    const childScreenKeys = parentNavToCategory[item.title];
    if (childScreenKeys) {
      return childScreenKeys.some(hasPermission);
    }
    return item.children?.some(child => {
      if (child.href) {
        const childScreenKey = navItemToPermissionMap[child.href];
        if (childScreenKey) return hasPermission(childScreenKey);
      }
      return false;
    }) ?? false;
  }

  return (item: NavItem): boolean => {
    if (!permissionsEnforced) return true;
    if (item.href && alwaysAccessibleItems.has(item.href)) return true;
    if (alwaysAccessibleItems.has(item.title)) return true;
    if (loading) return true;
    if (!userPermissions || !Array.isArray(userPermissions.teamIds)) return false;
    if (userPermissions.teamIds.includes('bypass_all_permissions')) return true;

    if (item.href) {
      const screenKey = navItemToPermissionMap[item.href];
      if (screenKey) return hasPermission(screenKey);
    }
    const screenKey = navItemToPermissionMap[item.title];
    if (screenKey) return hasPermission(screenKey);
    if (item.children && item.children.length > 0) return checkChildren(item);
    return false;
  };
}

/**
 * Hook to filter navigation items based on user permissions
 */
export function useFilteredNavigation(navigationItems: NavItem[]): NavItem[] {
  const { userPermissions, permissions, loading, permissionsEnforced = true } = usePermissions();
  const canAccessNavItem = useCanAccessNavItem();

  return useMemo(() => {
    if (!permissionsEnforced) return navigationItems;
    if (loading) return navigationItems;
    if (!userPermissions) return [];
    if (userPermissions && Array.isArray(userPermissions.teamIds) && userPermissions.teamIds.includes('bypass_all_permissions')) {
      return navigationItems;
    }
    const filterItems = (items: NavItem[]): NavItem[] => {
      return items
        .filter(item => canAccessNavItem(item))
        .map(item => {
          if (item.children) {
            const filteredChildren = filterItems(item.children);
            if (filteredChildren.length === 0) {
              return null;
            }
            return {
              ...item,
              children: filteredChildren,
            };
          }
          return item;
        })
        .filter((item): item is NavItem => item !== null);
    };
    return filterItems(navigationItems);
  }, [navigationItems, userPermissions, loading, permissionsEnforced, canAccessNavItem]);
}

/**
 * Hook to check specific sub-permissions for UI element visibility
 */
export function useFeaturePermission(subPermissionKey: string): boolean {
  const { hasSubPermission, loading, permissionsEnforced = true } = usePermissions();

  if (!permissionsEnforced) return true;

  // While loading, show the feature (will be validated on action)
  if (loading) return true;

  return hasSubPermission(subPermissionKey);
}

/**
 * Hook to check if user can perform specific actions on assets
 */
export function useAssetPermissions() {
  const { hasSubPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canView: true, canCreate: true, canEdit: true, canDelete: true };
    return {
      canView: loading || hasSubPermission('view-assets'),
      canCreate: !loading && hasSubPermission('create-assets'),
      canEdit: !loading && hasSubPermission('edit-assets'),
      canDelete: !loading && hasSubPermission('delete-assets'),
    };
  }, [hasSubPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on tasks
 */
export function useTaskPermissions() {
  const { hasSubPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canView: true, canCreate: true, canEdit: true, canAssign: true, isAdmin: true };
    return {
      canView: loading || hasSubPermission('view-tasks'),
      canCreate: !loading && hasSubPermission('create-tasks'),
      canEdit: !loading && hasSubPermission('edit-tasks'),
      canAssign: !loading && hasSubPermission('assign-tasks'),
      isAdmin: !loading && hasSubPermission('task-administrator'),
    };
  }, [hasSubPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on audits
 */
export function useAuditPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) {
      return {
        canViewAssignedAudits: true, canCarryOutAudit: true, canProcessAudit: true,
        canViewAllAudits: true, canCreateAudits: true, canEditAudits: true,
        canFlagAudits: true, canRaiseComments: true, canRaiseWorkItems: true,
        isAuditor: true, isManager: true,
      };
    }
    return {
      // Auditor permissions (mobile app)
      canViewAssignedAudits: loading || hasSubPermission('view-assigned-audits'),
      canCarryOutAudit: !loading && hasSubPermission('carry-out-audit'),
      canProcessAudit: !loading && hasSubPermission('process-audit'),

      // Manager permissions (main app)
      canViewAllAudits: loading || hasSubPermission('view-all-audits'),
      canCreateAudits: !loading && hasSubPermission('create-audits'),
      canEditAudits: !loading && hasSubPermission('edit-audits'),
      canFlagAudits: !loading && hasSubPermission('flag-audits'),
      canRaiseComments: !loading && hasSubPermission('raise-comments'),
      canRaiseWorkItems: !loading && hasSubPermission('raise-work-items'),

      // High-level access
      isAuditor: loading || hasPermission('audits-auditor'),
      isManager: loading || hasPermission('audits-manager'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can manage teams and permissions
 */
export function useTeamSettingsPermissions() {
  const { hasSubPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) {
      return {
        canViewTeams: true, canCreateTeams: true, canEditTeams: true,
        canDeleteTeams: true, canManageMembers: true, canManagePermissions: true,
      };
    }
    return {
      canViewTeams: loading || hasSubPermission('view_teams'),
      canCreateTeams: !loading && hasSubPermission('manage_teams'),
      canEditTeams: !loading && hasSubPermission('manage_teams'),
      canDeleteTeams: !loading && hasSubPermission('manage_teams'),
      canManageMembers: !loading && hasSubPermission('manage_teams'),
      canManagePermissions: !loading && hasSubPermission('manage_permissions'),
    };
  }, [hasSubPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on additions transactions
 */
export function useAdditionsPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canCreate: true, canEdit: true };
    return {
      canAccess: loading || hasPermission('additions'),
      canView: loading || hasSubPermission('view-additions'),
      canCreate: !loading && hasSubPermission('create-additions'),
      canEdit: !loading && hasSubPermission('edit-additions'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on depreciation transactions
 */
export function useDepreciationPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canCreate: true, canEdit: true };
    return {
      canAccess: loading || hasPermission('depreciation'),
      canView: loading || hasSubPermission('view-depreciation'),
      canCreate: !loading && hasSubPermission('create-depreciation'),
      canEdit: !loading && hasSubPermission('edit-depreciation'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on revaluation transactions
 */
export function useRevaluationPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canCreate: true, canEdit: true };
    return {
      canAccess: loading || hasPermission('revaluation'),
      canView: loading || hasSubPermission('view-revaluation'),
      canCreate: !loading && hasSubPermission('create-revaluation'),
      canEdit: !loading && hasSubPermission('edit-revaluation'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on locations
 */
export function useLocationsPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('locations'),
      canView: loading || hasSubPermission('view-locations'),
      canManage: !loading && hasSubPermission('edit-locations'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on categories
 */
export function useCategoriesPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('categories'),
      canView: loading || hasSubPermission('view-categories'),
      canManage: !loading && hasSubPermission('edit-categories'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on equipment
 */
export function useEquipmentPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('equipment'),
      canView: loading || hasSubPermission('view-equipment'),
      canManage: !loading && hasSubPermission('edit-equipment'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on disposal reasons
 */
export function useDisposalReasonsPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('disposal-reasons'),
      canView: loading || hasSubPermission('view-disposal-reasons'),
      canManage: !loading && hasSubPermission('edit-disposal-reasons'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on feature settings
 */
export function useFeatureSettingsPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('feature-settings'),
      canView: loading || hasSubPermission('view-features'),
      canManage: !loading && hasSubPermission('toggle-features'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can perform specific actions on asset options
 */
export function useAssetOptionsPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('asset-options'),
      canView: loading || hasSubPermission('view-asset-options'),
      canManage: !loading && hasSubPermission('edit-asset-options'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can import assets
 */
export function useImportPermissions() {
  const { hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canImportAssets: true, canImportTransactions: true };
    return {
      canImportAssets: loading || hasPermission('import-assets'),
      canImportTransactions: loading || hasPermission('import-transactions'),
    };
  }, [hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can access period management
 */
export function usePeriodManagementPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true, canManage: true };
    return {
      canAccess: loading || hasPermission('period-management'),
      canView: loading || hasSubPermission('view_periods'),
      canManage: !loading && hasSubPermission('manage_periods'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check if user can access integration logs
 */
export function useIntegrationLogPermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) return { canAccess: true, canView: true };
    return {
      canAccess: loading || hasPermission('integration-log'),
      canView: loading || hasSubPermission('view_integration_log'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}

/**
 * Hook to check compliance permissions with granular sub-permissions
 */
export function useCompliancePermissions() {
  const { hasSubPermission, hasPermission, loading, permissionsEnforced = true } = usePermissions();

  return useMemo(() => {
    if (!permissionsEnforced) {
      return {
        canAccess: true, canViewDashboard: true, canManage: true,
        canViewRegulations: true, canManageRegulations: true,
        canViewSchedule: true, canManageSchedule: true,
      };
    }
    return {
      canAccess: loading || hasPermission('compliance'),
      canViewDashboard: loading || hasSubPermission('view_compliance'),
      canManage: !loading && hasSubPermission('manage_compliance'),
      canViewRegulations: loading || hasSubPermission('view_regulations'),
      canManageRegulations: !loading && hasSubPermission('manage_regulations'),
      canViewSchedule: loading || hasSubPermission('view_schedule'),
      canManageSchedule: !loading && hasSubPermission('manage_schedule'),
    };
  }, [hasSubPermission, hasPermission, loading, permissionsEnforced]);
}
